/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { useDashboardStats } from "../../store/useDashboardStats";
import dayjs from "dayjs";

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  monthRangeProp?: Date;
  yearRangeProp?: Date;
  className?: string;
}

export const TotalDroppieRevenueChart = React.memo(
  ({ className, periodProp, yearRangeProp, monthRangeProp }: Props) => {
    /** Hooks */
    const {
      totalDroppieRevenueTrendingDrops,
      getTotalDroppieRevenueTrendingDrops,
    } = useDashboardStats();
    /** State */
    const [period, setPeriod] = useState<"Monthly" | "Yearly">("Yearly");
    const [weeklyRange, setWeeklyRange] = useState(new Date());

    /** Callbacks */
    useEffect(() => {
      if (period === "Monthly") {
        getTotalDroppieRevenueTrendingDrops(weeklyRange, "monthly");
      } else if (period === "Yearly") {
        getTotalDroppieRevenueTrendingDrops(weeklyRange, "yearly");
      }
    }, [weeklyRange, period]);

    useEffect(() => {
      if (periodProp === "Weekly" && monthRangeProp) {
        setPeriod("Monthly");
        setWeeklyRange(monthRangeProp);
      }
      if (periodProp === "Monthly" && yearRangeProp) {
        setPeriod("Yearly");
        setWeeklyRange(yearRangeProp);
      }
    }, [periodProp, yearRangeProp, monthRangeProp]);

    const totalRevenue =
      totalDroppieRevenueTrendingDrops?.find(
        (item: any) => item?.name === "Total Revenue"
      )?.value ?? null;

    return (
      <div
        className={`p-7 w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-10 ${className}`}
      >
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-row items-start justify-start">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
              Total Droppie Revenue
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start justify-start mb-12">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "Monthly"}
                  onClick={() => setPeriod("Monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "Yearly"}
                  onClick={() => {
                    const todayDate = new Date();
                    setWeeklyRange(
                      new Date(
                        `${todayDate.getFullYear()}-${
                          todayDate.getMonth() + 1
                        }-15`
                      )
                    );
                    setPeriod("Yearly");
                  }}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
            {period === "Monthly" ? (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 sm:ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {dayjs(weeklyRange).format("MMMM")}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
                    const currentYear = new Date(weeklyRange).getFullYear();
                    return (
                      <MenuItem
                        disabled={weeklyRange.getMonth() + 1 === month}
                        onClick={() =>
                          setWeeklyRange(new Date(`${currentYear}-${month}-15`))
                        }
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {dayjs(`${currentYear}-${month}-05`).format("MMMM")}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            ) : null}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 sm:ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {weeklyRange.getFullYear()}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  onClick={() =>
                    setWeeklyRange(
                      new Date(
                        weeklyRange.setFullYear(new Date().getFullYear())
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear()}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setWeeklyRange(
                      new Date(
                        weeklyRange.setFullYear(new Date().getFullYear() - 1)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 1}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setWeeklyRange(
                      new Date(
                        weeklyRange.setFullYear(new Date().getFullYear() - 2)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 2}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setWeeklyRange(
                      new Date(
                        weeklyRange.setFullYear(new Date().getFullYear() - 3)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 3}
                </MenuItem>
              </MenuList>
            </Menu>
          </div>
        </div>
        <div className="text-center font-satoshi font-bold text-xl text-tailgrids-black mb-2">
          Total Revenue:{" "}
          {new Intl.NumberFormat("nl-NL", {
            style: "currency",
            currency: "EUR",
          }).format(Number(Number(totalRevenue ?? 0) / 100) ?? 0)}
        </div>
        {/* @ts-ignore */}
        {totalDroppieRevenueTrendingDrops?.length ? (
          <Chart
            {...streamCompositionsDonutChartData}
            // @ts-ignore
            options={{
              ...streamCompositionsDonutChartData?.options,
              labels: (
                totalDroppieRevenueTrendingDrops?.filter(
                  (item: any) => item?.name !== "Total Revenue"
                ) ?? []
              )?.map((item: any) => item?.name),
              plotOptions: {
                ...streamCompositionsDonutChartData?.options?.plotOptions,
              },
            }}
            series={(
              totalDroppieRevenueTrendingDrops?.filter(
                (item: any) => item?.name !== "Total Revenue"
              ) ?? []
            )?.map((item: any) => Number(item?.value ?? 0))}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
              No data found
            </p>
          </div>
        )}
      </div>
    );
  }
);

const streamCompositionsDonutChartData = {
  type: "pie",
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: any, opts: any) {
        const value = opts.w.globals.series[opts.seriesIndex];
        return `${
          opts.w.globals.labels[opts.seriesIndex]
        }\n${new Intl.NumberFormat("nl-NL", {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value / 100)}`;
      },
    },
    colors: [
      "#83EAF4",
      "#56B5B1",
      "#3C5219",
      "#002A27",
      "#8DAC83",
      "#8898B6",
      "#A8BF8A",
      "#84BAFF",
      "#93C1DA",
      "#97D2F7",
      "#142409",
      "#81CE7C",
      "#7DEA96",
      "#98E9FE",
      "#92E2DD",
      "#3F5747",
      "#3F4A14",
      "#8E9189",
      "#A199AD",
      "#A7CF85",
    ],
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "14px",
      fontFamily: "Satoshi",
      fontWeight: 500,
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    plotOptions: {
      pie: {
        startAngle: 0,
        endAngle: 360,
        expandOnClick: true,
        offsetX: 0,
        offsetY: 0,
      },
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: function (value: number) {
          return new Intl.NumberFormat("nl-NL", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value / 100);
        },
      },
    },
  },
};
